import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';

// Telnyx Wireless admin: list every SIM/eSIM on the account, see
// per-billing-period consumption + group caps, and order more.
// All API calls go through `${API}/cellular/telnyx/*` which proxies to
// Telnyx server-side so the API key never reaches the browser.

const API = (window as any).REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

type Money = { unit: 'MB' | 'GB'; amount: string } | null;

interface Sim {
  id: string;
  type: 'esim' | 'physical' | string;
  status: { value: string; reason: string };
  iccid: string;
  msisdn: string | null;
  tags: string[];
  current_billing_period_consumed_data: Money;
  data_limit: Money;
  sim_card_group_id: string | null;
  esim_installation_status: string | null;
  created_at: string;
}

interface Group {
  id: string;
  name: string;
  data_limit: Money;
  consumed_data: Money;
  default: boolean;
}

function authHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('intrlock_token') ?? ''}`,
  };
}

function mbOf(m: Money): number {
  if (!m) return 0;
  const v = Number(m.amount);
  return m.unit === 'GB' ? v * 1024 : v;
}

function formatMB(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(mb * 1024).toFixed(0)} KB`;
}

export const CellularAdmin: React.FC = () => {
  const [sims, setSims] = useState<Sim[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [orderOpen, setOrderOpen] = useState<'esim' | 'physical' | null>(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [simsRes, groupsRes] = await Promise.all([
        fetch(`${API}/cellular/telnyx/sim-cards?page_size=100`, { headers: authHeaders() }),
        fetch(`${API}/cellular/telnyx/sim-card-groups`, { headers: authHeaders() }),
      ]);
      if (!simsRes.ok) throw new Error(`Sims: HTTP ${simsRes.status}`);
      if (!groupsRes.ok) throw new Error(`Groups: HTTP ${groupsRes.status}`);
      const simsJson = await simsRes.json();
      const groupsJson = await groupsRes.json();
      setSims(simsJson.data ?? []);
      setGroups(groupsJson.data ?? []);
    } catch (e: any) {
      setErr(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => { load(); }, []);

  const groupName = (gid: string | null) =>
    groups.find((g) => g.id === gid)?.name ?? '—';

  const totalConsumedMB = sims.reduce((s, x) => s + mbOf(x.current_billing_period_consumed_data), 0);
  const totalLimitMB = groups.reduce((s, g) => s + mbOf(g.data_limit), 0);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Cellular Provisioning</h1>
          <p className="text-sm text-gray-500">Telnyx Wireless SIMs powering Intrlock kiosks, bridges, and panels.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={load} disabled={loading}>↻ Refresh</Button>
          <Button onClick={() => setOrderOpen('esim')}>+ Order eSIM</Button>
          <Button variant="secondary" onClick={() => setOrderOpen('physical')}>+ Order Physical SIM</Button>
        </div>
      </div>

      {err && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{err}</div>
      )}

      <div className="grid md:grid-cols-3 gap-3">
        <Stat label="Total SIMs" value={sims.length} />
        <Stat label="Consumed this period" value={formatMB(totalConsumedMB)} />
        <Stat label="Group caps total" value={totalLimitMB ? formatMB(totalLimitMB) : 'unmetered'} />
      </div>

      {/* Groups */}
      <Card>
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">SIM Groups</h3>
          <span className="text-xs text-gray-500">Shared data caps apply across all SIMs in a group</span>
        </div>
        <div className="divide-y">
          {groups.map((g) => {
            const consumedMB = mbOf(g.consumed_data);
            const limitMB = mbOf(g.data_limit);
            const pct = limitMB ? Math.min(100, (consumedMB / limitMB) * 100) : 0;
            return (
              <div key={g.id} className="p-4 flex items-center gap-4">
                <div className="flex-1">
                  <div className="font-medium flex items-center gap-2">
                    {g.name}
                    {g.default && <Badge color="gray">default</Badge>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 font-mono">{g.id}</div>
                </div>
                <div className="w-72">
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>{formatMB(consumedMB)} used</span>
                    <span>{limitMB ? `cap ${formatMB(limitMB)}` : 'no cap'}</span>
                  </div>
                  {limitMB > 0 && (
                    <div className="h-2 bg-gray-200 rounded overflow-hidden">
                      <div
                        className={`h-full ${pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  )}
                </div>
                <Button variant="secondary" onClick={() => promptRaiseCap(g, load)}>Change cap</Button>
              </div>
            );
          })}
          {!groups.length && !loading && (
            <div className="p-4 text-sm text-gray-500">No SIM groups yet.</div>
          )}
        </div>
      </Card>

      {/* SIM cards */}
      <Card>
        <div className="p-4 border-b">
          <h3 className="font-semibold">SIM Inventory ({sims.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Tag / Label</th>
                <th className="text-left p-3">ICCID</th>
                <th className="text-left p-3">MSISDN</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Used this period</th>
                <th className="text-left p-3">Group</th>
                <th className="text-left p-3">Activated</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sims.map((s) => (
                <tr key={s.id}>
                  <td className="p-3">
                    <Badge color={s.type === 'esim' ? 'blue' : 'gray'}>{s.type}</Badge>
                  </td>
                  <td className="p-3">{s.tags?.length ? s.tags.join(', ') : <span className="text-gray-400">—</span>}</td>
                  <td className="p-3 font-mono text-xs">{s.iccid}</td>
                  <td className="p-3 font-mono text-xs">{s.msisdn ?? '—'}</td>
                  <td className="p-3">
                    <Badge color={s.status?.value === 'enabled' ? 'green' : 'orange'}>
                      {s.status?.value ?? 'unknown'}
                    </Badge>
                  </td>
                  <td className="p-3">{formatMB(mbOf(s.current_billing_period_consumed_data))}</td>
                  <td className="p-3 text-xs">{groupName(s.sim_card_group_id)}</td>
                  <td className="p-3 text-xs text-gray-500">{s.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
              {!sims.length && !loading && (
                <tr><td colSpan={8} className="p-4 text-center text-sm text-gray-500">No SIMs yet — order one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {orderOpen && (
        <OrderModal
          type={orderOpen}
          groups={groups}
          onClose={() => setOrderOpen(null)}
          onSuccess={() => { setOrderOpen(null); load(); }}
        />
      )}
    </div>
  );
};

function promptRaiseCap(g: Group, refresh: () => void) {
  const current = g.data_limit?.amount ?? '';
  const unit = g.data_limit?.unit ?? 'MB';
  const next = prompt(`Set data cap for "${g.name}" (current: ${current ? `${current} ${unit}` : 'no cap'}).\nEnter amount in MB, or 0 for no cap.`, current || '');
  if (next == null) return;
  const amount = Number(next);
  if (Number.isNaN(amount) || amount < 0) { alert('Invalid amount.'); return; }
  (async () => {
    const body = amount === 0
      ? { data_limit: null }
      : { data_limit: { unit: 'MB', amount: String(amount) } };
    const res = await fetch(`${API}/cellular/telnyx/sim-card-groups/${g.id}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      alert(`Failed to update: ${await res.text()}`);
      return;
    }
    refresh();
  })();
}

interface OrderModalProps {
  type: 'esim' | 'physical';
  groups: Group[];
  onClose: () => void;
  onSuccess: () => void;
}

const OrderModal: React.FC<OrderModalProps> = ({ type, groups, onClose, onSuccess }) => {
  const [qty, setQty] = useState(1);
  const [groupId, setGroupId] = useState(groups.find((g) => g.default)?.id ?? groups[0]?.id ?? '');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);
  // Physical-only fields
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [business, setBusiness] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postal, setPostal] = useState('');
  const [country, setCountry] = useState('US');

  const submit = async () => {
    setBusy(true);
    try {
      const body: any = { type, quantity: qty };
      if (groupId) body.group_id = groupId;
      if (tags.trim()) body.tags = tags.split(',').map((t) => t.trim()).filter(Boolean);
      if (type === 'physical') {
        body.shipping_address = {
          first_name: first, last_name: last, business_name: business || undefined,
          street_address: street, city, state, postal_code: postal, country_code: country,
        };
      }
      const res = await fetch(`${API}/cellular/telnyx/order-sims`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await res.text());
      const j = await res.json();
      alert(`Ordered ${j.ordered} ${j.type} SIM${j.ordered === 1 ? '' : 's'}.`);
      onSuccess();
    } catch (e: any) {
      alert(`Order failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold">Order {type === 'esim' ? 'eSIM' : 'Physical SIM'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <Row label="Quantity">
            <input type="number" min={1} max={50} value={qty}
              onChange={(e) => setQty(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
              className={inputCls} />
          </Row>
          <Row label="SIM group">
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className={inputCls}>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}{g.default ? ' (default)' : ''}</option>)}
            </select>
          </Row>
          <Row label="Tag (optional)">
            <input value={tags} onChange={(e) => setTags(e.target.value)}
              placeholder="e.g. Mila Towers Kiosk" className={inputCls} />
          </Row>

          {type === 'physical' && (
            <>
              <div className="pt-2 border-t mt-2 text-xs uppercase text-gray-500">Shipping address</div>
              <div className="grid grid-cols-2 gap-2">
                <input value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name" className={inputCls} />
                <input value={last} onChange={(e) => setLast(e.target.value)} placeholder="Last name" className={inputCls} />
              </div>
              <input value={business} onChange={(e) => setBusiness(e.target.value)} placeholder="Business (optional)" className={inputCls} />
              <input value={street} onChange={(e) => setStreet(e.target.value)} placeholder="Street address" className={inputCls} />
              <div className="grid grid-cols-3 gap-2">
                <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className={inputCls} />
                <input value={state} onChange={(e) => setState(e.target.value)} placeholder="State" className={inputCls} />
                <input value={postal} onChange={(e) => setPostal(e.target.value)} placeholder="ZIP" className={inputCls} />
              </div>
              <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country (ISO)" className={inputCls} />
            </>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>{busy ? 'Ordering…' : `Order ${qty}`}</Button>
        </div>
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="text-xs uppercase text-gray-500 mb-1">{label}</div>
    {children}
  </div>
);

const Stat: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <div className="text-xs uppercase text-gray-500">{label}</div>
    <div className="text-2xl font-semibold mt-1">{value}</div>
  </div>
);

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-accent-500 focus:border-accent-500';
